import type { OptimizationRun } from '../types/optimization'
import type { OptimizationOutputSummary } from '../types/optimizationOutput'

const idOf = (value: any) => String(value?.item_id ?? value?.itemId ?? value?.id ?? '')
const number = (value: any) => value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null
const ordinal = (value: number) => String(value).padStart(2, '0')
const description = (item: any) => String(item?.description || item?.product_name || item?.sku || item?._viewerOriginalId || item?.id || 'Colis').trim()
const groupList = (run: any): any[] => run?.result?.palletGroups ?? run?.result?.pallet_groups ?? []
const dimensions = (item: any) => [item?.length, item?.width, item?.height].map(Number).sort((a, b) => a - b)

export function cargoLineLabels(items: any[]): Map<string, string> {
  const totals = new Map<string, number>(), seen = new Map<string, number>()
  items.forEach(item => totals.set(description(item), (totals.get(description(item)) ?? 0) + 1))
  return new Map(items.map(item => {
    const name = description(item), index = (seen.get(name) ?? 0) + 1
    seen.set(name, index)
    return [item.id, item._viewerLabel ?? (totals.get(name)! > 1 ? `${name}_${ordinal(index)}` : name)]
  }))
}

/** Give calculation inputs distinct line identities even when users reuse a SKU. */
export function uniqueCargoLineIds<T extends { id: string; sku?: string | null }>(items: T[]): T[] {
  const frequencies = new Map<string, number>()
  items.forEach(item => frequencies.set(item.id, (frequencies.get(item.id) ?? 0) + 1))
  const reserved = new Set(items.map(item => item.id))
  return items.map((item, index) => {
    if (frequencies.get(item.id) === 1) return item
    let id = `${item.id}-line-${index + 1}`
    while (reserved.has(id)) id += '-copy'
    reserved.add(id)
    return { ...item, id, sku: item.sku || item.id }
  })
}

/**
 * Historical runs may reuse an ID across cargo lines. This read-only projection
 * separates their UI identities using saved unit attributes, never ID prefixes
 * alone. A genuinely ambiguous historical attribution stays unavailable.
 */
export function presentCargoLines(source: OptimizationRun, summary?: OptimizationOutputSummary) {
  const originalItems: any[] = source.input.items || []
  if (originalItems.some(item => item._viewerLine)) return { run: source, summary }
  const unique = uniqueCargoLineIds(originalItems)
  const duplicateIds = unique.some((item, index) => item.id !== originalItems[index].id)
  const labels = cargoLineLabels(unique)
  const items = unique.map((item, index) => ({ ...item, _viewerLine: true,
    _viewerOriginalId: originalItems[index].id, _viewerLabel: labels.get(item.id) }))
  const loaded = new Map(items.map(item => [item.id, 0]))
  const rejected = new Map(items.map(item => [item.id, 0]))
  const ambiguous = new Set<string>()
  const originalIds = new Set(originalItems.map(item => item.id))
  const sourceId = (unit: any) => {
    const id = idOf(unit)
    const explicit = unit?.source_id ?? unit?.metadata?.source_id
    if (explicit != null && originalIds.has(String(explicit))) return String(explicit)
    if (originalIds.has(id)) return id
    const parts = id.split('_')
    while (parts.length > 1) { parts.pop(); if (originalIds.has(parts.join('_'))) return parts.join('_') }
    return id
  }
  const resolve = (unit: any, palletized: boolean, unloading = false): any => {
    let candidates = items.filter(item => item._viewerOriginalId === sourceId(unit))
    if (candidates.length <= 1) return candidates[0]
    const explicitLine = unit?.source_line_id ?? unit?.metadata?.source_line_id ?? unit?._dndId
    if (explicitLine) {
      const match = candidates.find(item => item._dndId === explicitLine || item.id === explicitLine)
      if (match) return match
    }
    const narrow = (predicate: (item: any) => boolean) => {
      const matches = candidates.filter(predicate)
      if (matches.length) candidates = matches
    }
    narrow(item => Boolean(item.requires_pallet || item.is_palletized) === palletized)
    const dims = dimensions(unit)
    if (dims.every(value => Number.isFinite(value) && value > 0)) {
      narrow(item => dimensions(item).every((value, index) => Math.abs(value - dims[index]) < 0.01))
    }
    const weight = number(unit?.weight ?? unit?.rejection_summary?.unit_weight_kg)
    if (weight != null) narrow(item => Math.abs(Number(item.weight) - weight) < 0.001)
    if (unit?.color) narrow(item => item.color?.toLowerCase() === unit.color.toLowerCase())
    if (unit?.unloading_order != null) narrow(item => item.unloading_order === unit.unloading_order)
    if (unloading) narrow(item => Number(item.quantity ?? 1) > (loaded.get(item.id) ?? 0) + (rejected.get(item.id) ?? 0))
    if (candidates.length === 1) return candidates[0]
    candidates.forEach(item => ambiguous.add(item.id))
    return undefined
  }
  const groups = groupList(source).map(group => {
    const children = (group.children_positions?.length ? group.children_positions :
      (group.children_item_ids || []).map((id: string) => ({ item_id: id }))).map((child: any) => {
      const line = resolve(child, true)
      return { ...child, source_id: line?.id ?? child.source_id,
        description: line?._viewerLabel ?? child.description }
    })
    return { ...group, children_positions: group.children_positions?.length ? children : [], _viewerChildren: children }
  })
  const groupMap = new Map(groups.map(group => [group.pallet_id, group]))
  const placements = (source.result?.placements || []).map((placement: any) => {
    const group = groupMap.get(idOf(placement))
    if (group) {
      group._viewerChildren.forEach((child: any) => {
        if (loaded.has(child.source_id)) loaded.set(child.source_id, loaded.get(child.source_id)! + 1)
      })
      return placement
    }
    const line = resolve(placement, false)
    if (line) loaded.set(line.id, loaded.get(line.id)! + 1)
    return { ...placement, source_id: line?.id ?? placement.source_id,
      metadata: { ...placement.metadata, source_id: line?.id ?? placement.metadata?.source_id } }
  })
  const rawUnloaded: any[] | undefined = (source.result as any)?.unloadedItems ?? (source.result as any)?.unloaded_items
  const unloaded = rawUnloaded?.map(unit => {
    if (groupMap.has(idOf(unit))) return unit
    const line = resolve(unit, false, true)
    if (line) rejected.set(line.id, rejected.get(line.id)! + Number(unit.quantity ?? 1))
    return { ...unit, source_id: line?.id ?? unit.source_id,
      metadata: { ...unit.metadata, source_id: line?.id ?? unit.metadata?.source_id } }
  })
  const projectedItems = items.map(item => ({ ...item,
    _viewerLoaded: ambiguous.has(item.id) ? null : loaded.get(item.id) ?? 0 }))
  const run = { ...source, input: { ...source.input, items: projectedItems }, result: source.result ? {
    ...source.result, placements, palletGroups: groups, pallet_groups: groups,
    ...(unloaded ? { unloadedItems: unloaded, unloaded_items: unloaded } : {}),
  } : source.result } as OptimizationRun
  if (!summary || !duplicateIds) return { run, summary }
  // Current API/exports already use the repaired source-line identities.
  // Keep their canonical counts and real rejection reasons authoritative.
  if (summary.cargo.articles.every((article, index) => article.id === projectedItems[index]?.id)) return { run, summary }

  const placementKey = (unit: any) => `${idOf(unit)}:${unit.x ?? unit.position_x ?? unit.position?.x ?? 0}:${unit.y ?? unit.position_y ?? unit.position?.y ?? 0}:${unit.z ?? unit.position_z ?? unit.position?.z ?? 0}`
  const placementMap = new Map(placements.map(unit => [placementKey(unit), unit]))
  const articles = projectedItems.map((item, index) => {
    const article = summary.cargo.articles[index]
    const count = item._viewerLoaded
    const remaining = count == null ? null : Math.max(0, Number(item.quantity ?? 1) - count)
    return { ...article, id: item.id, description: item._viewerLabel!, color: item.color ?? article?.color,
      requested: Number(item.quantity ?? 1), loaded: count, remaining,
      reasons: remaining ? article?.reasons ?? [] : [], unit_mass_kg: item.weight ?? article?.unit_mass_kg ?? null }
  })
  const complete = articles.every(article => article.loaded != null)
  const correctedSummary = { ...summary, cargo: { ...summary.cargo, articles,
    loaded: complete ? articles.reduce((sum, article) => sum + article.loaded!, 0) : null,
    remaining: complete ? articles.reduce((sum, article) => sum + article.remaining!, 0) : null,
  }, placements: summary.placements.map(placement => {
    const unit: any = placementMap.get(placementKey({ ...placement, ...placement.position_mm }))
    const group = groupMap.get(placement.item_id)
    return { ...placement, source_item_id: unit?.metadata?.source_id ?? placement.source_item_id,
      pallet: placement.pallet ? { ...placement.pallet, children: placement.pallet.children.map((child, index) => ({
        ...child, source_item_id: group?.children_positions[index]?.source_id ?? child.source_item_id,
        description: group?.children_positions[index]?.description ?? child.description,
      })) } : placement.pallet }
  }) } as OptimizationOutputSummary
  return { run, summary: correctedSummary }
}

export function palletDisplayLabels(run: OptimizationRun): Map<string, string> {
  const items = new Map(run.input.items.map(item => [item.id, item]))
  const seen = new Map<string, number>()
  return new Map(groupList(run).map(group => {
    const names = [...new Set<string>((group._viewerChildren ?? group.children_positions ?? []).map((child: any) =>
      description(items.get(child.source_id) ?? child))) ]
    const name = names.length === 1 ? names[0] : names.length > 1 ? 'mixte' : 'palette'
    const index = (seen.get(name) ?? 0) + 1; seen.set(name, index)
    return [group.pallet_id, `pallet_${name}_${ordinal(index)}`]
  }))
}

export function unloadedDisplayLabels(items: any[]): Map<any, string> {
  const seen = new Map<string, number>()
  return new Map(items.map(item => {
    const name = description(item), index = (seen.get(name) ?? 0) + 1
    seen.set(name, index)
    return [item, `${name}_${ordinal(index)}`]
  }))
}
